import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useNotification } from '../hooks/useNotification';
import axiosInstance from '../lib/axios';
import {
  InputFieldDef,
  InputFormat,
  InputFormatListItem,
  SaveInputFormatInput,
} from '../types/admin';
import { llmModelInputKeys } from './llm-model-input.service';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: string[];
}

export class InputFormatValidationError extends Error {
  errors: string[];

  constructor(message: string, errors: string[]) {
    super(message);
    this.errors = errors;
  }
}

export const inputFormatKeys = {
  all: ['llm-input-formats'] as const,
  list: () => [...inputFormatKeys.all, 'list'] as const,
  detail: (publicId?: string) => [...inputFormatKeys.all, 'detail', publicId ?? ''] as const,
};

const BASE = '/v1/admin/llm-input-formats';

export const useInputFormats = () =>
  useQuery({
    queryKey: inputFormatKeys.list(),
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<InputFormatListItem[]>>(BASE);
      return response.data.data ?? [];
    },
  });

export const useInputFormat = (publicId?: string) =>
  useQuery({
    queryKey: inputFormatKeys.detail(publicId),
    enabled: !!publicId,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<InputFormat>>(`${BASE}/${publicId}`);
      return response.data.data as InputFormat;
    },
  });

function toValidationError(error: unknown, fallback: string): unknown {
  const axiosError = error as AxiosError<ApiResponse>;
  const errors = axiosError.response?.data?.errors ?? [];
  if (axiosError.response?.status === 400 && errors.length > 0) {
    return new InputFormatValidationError(axiosError.response.data?.message ?? fallback, errors);
  }
  return error;
}

function useInvalidateFormats() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: inputFormatKeys.all });
    queryClient.invalidateQueries({ queryKey: llmModelInputKeys.all });
  };
}

function notifyError(
  notification: ReturnType<typeof useNotification>,
  error: unknown,
  fallback: string
) {
  if (error instanceof InputFormatValidationError) return;
  const axiosError = error as AxiosError<ApiResponse>;
  notification.error(
    axiosError.response?.data?.message || axiosError.response?.data?.error || fallback
  );
}

export const useSaveInputFormat = () => {
  const invalidate = useInvalidateFormats();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ publicId, ...body }: SaveInputFormatInput) => {
      try {
        const response = publicId
          ? await axiosInstance.put<ApiResponse<InputFormat>>(`${BASE}/${publicId}`, body)
          : await axiosInstance.post<ApiResponse<InputFormat>>(BASE, body);
        return response.data.data as InputFormat;
      } catch (error) {
        throw toValidationError(error, 'Input format validation failed');
      }
    },
    onSuccess: () => {
      invalidate();
      notification.success('Input format saved');
    },
    onError: (error) => notifyError(notification, error, 'Failed to save input format'),
  });
};

export const useDuplicateInputFormat = () => {
  const invalidate = useInvalidateFormats();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      publicId,
      code,
      name,
    }: {
      publicId: string;
      code: string;
      name: string;
    }) => {
      const response = await axiosInstance.post<ApiResponse<InputFormat>>(
        `${BASE}/${publicId}/duplicate`,
        { code, name }
      );
      return response.data.data as InputFormat;
    },
    onSuccess: () => {
      invalidate();
      notification.success('Input format duplicated');
    },
    onError: (error) => notifyError(notification, error, 'Failed to duplicate input format'),
  });
};

export const useDeleteInputFormat = () => {
  const invalidate = useInvalidateFormats();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (publicId: string) => {
      await axiosInstance.delete(`${BASE}/${publicId}`);
    },
    onSuccess: () => {
      invalidate();
      notification.success('Input format deleted');
    },
    onError: (error) => notifyError(notification, error, 'Failed to delete input format'),
  });
};

export interface ImportSchemasInput {
  inputSchema?: string;
  outputSchema?: string;
}

export interface ImportSchemasResult {
  fields: InputFieldDef[];
  warnings: string[];
}

export const useImportInputFormatSchemas = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async (body: ImportSchemasInput) => {
      const response = await axiosInstance.post<ApiResponse<ImportSchemasResult>>(
        `${BASE}/import-schema`,
        body
      );
      return response.data.data as ImportSchemasResult;
    },
    onError: (error) => notifyError(notification, error, 'Failed to convert JSON Schema'),
  });
};
